import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { PortalDsButton } from "src/components/portal-design-system/portal-ds-button";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import { cn } from "src/lib/utils";
import { formatCurrency } from "../../utils/billing-utils";
import type { CommissionAdjustment } from "../types/commissions.types";
import { CommissionTableEmptyState } from "./commission-table-empty-state";

interface AdjustmentsTableProps {
  adjustments: CommissionAdjustment[];
  onRowClick?: (adjustment: CommissionAdjustment) => void;
  onDelete?: (adjustment: CommissionAdjustment) => void;
  deletingId?: string | null;
}

const TARGET_TYPE_LABELS: Record<CommissionAdjustment["targetType"], string> = {
  user: "Activation Team",
  external_rep: "External Rep",
  internal_referral: "Internal Referral",
};

const AdjustmentsTable: React.FC<AdjustmentsTableProps> = ({
  adjustments,
  onRowClick,
  onDelete,
  deletingId = null,
}) => {
  const columnCount = onDelete ? 6 : 5;

  return (
    <div className={portalDs.tableContainer}>
      <Table>
        <TableHeader>
          <TableRow className={portalDs.tableHead}>
            <TableHead className={portalDs.tableHeadCell}>Target</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Type</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Period</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Amount / Multiplier</TableHead>
            <TableHead className={portalDs.tableHeadCell}>Reason</TableHead>
            {onDelete ? (
              <TableHead className={cn(portalDs.tableHeadCell, "text-right")}>
                Actions
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {adjustments.length === 0 ? (
            <CommissionTableEmptyState
              colSpan={columnCount}
              title="No adjustments yet"
              description='Use "Add adjustment" to create penalties, credits, or one-off corrections.'
            />
          ) : (
            adjustments.map((adj) => (
              <TableRow
                key={adj.id}
                className={cn(
                  portalDs.tableBodyRow,
                  onRowClick && portalDs.tableBodyRowInteractive
                )}
                onClick={() => onRowClick?.(adj)}
              >
                <TableCell className={cn(portalDs.tableCell, "font-medium")}>
                  {adj.targetName}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                  {TARGET_TYPE_LABELS[adj.targetType]}
                </TableCell>
                <TableCell className={portalDs.tableCell}>
                  Q{adj.quarter} {adj.year}
                </TableCell>
                <TableCell className={portalDs.tableCell}>
                  {adj.amount != null
                    ? formatCurrency(adj.amount)
                    : adj.multiplier != null
                      ? `×${adj.multiplier}`
                      : "—"}
                </TableCell>
                <TableCell className={cn(portalDs.tableCell, portalDs.tableCellMuted)}>
                  {adj.reason}
                </TableCell>
                {onDelete ? (
                  <TableCell className={cn(portalDs.tableCell, "text-right")}>
                    <PortalDsButton
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={deletingId === adj.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete(adj);
                      }}
                    >
                      {deletingId === adj.id ? "Removing…" : "Remove"}
                    </PortalDsButton>
                  </TableCell>
                ) : null}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default AdjustmentsTable;
